'use client';

import { useMemo, useState } from 'react';

import { ICandidateProfile, IElectionPosition } from '@/.generated';
import { Container } from '@/components/primitives/Container/Container';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { IBaseComponentProps } from '@/lib/types';
import { getCSLPAttributes } from '@/utils/type-guards';
import { trackAdobeClick, trackAdobeDownload, getDocumentNameFromUrl } from '@/utils/adobe';
import { electionCandidatesVariants } from './ElectionCandidates.styles';

export interface ElectionCandidatesClientProps extends IBaseComponentProps {
  candidates: ICandidateProfile[];
  votingAreas: string[];
  associationPositions: IElectionPosition[];
  districtPositions: IElectionPosition[];
  _metadata?: { uid?: string };
}

// A position heading plus the candidates that belong to it (already vote-sorted)
type PositionGroup = {
  uid: string;
  title: string;
  candidates: ICandidateProfile[];
};

// Format a vote total with thousands separators and two decimals (e.g. 1,264.13)
const formatVotes = (votes: number): string =>
  votes.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Display name:  compose from name fields, otherwise the authored entry title
const getCandidateName = (candidate: ICandidateProfile): string =>
  [candidate.first_name, candidate.last_name].filter(Boolean).join(', ') || candidate.title;

export const ElectionCandidatesClient = (props: ElectionCandidatesClientProps) => {
  const {
    componentName,
    _metadata,
    candidates,
    votingAreas,
    associationPositions,
    districtPositions,
  } = props;

  const {
    globalLabels: { election_candidates_labels },
  } = useGlobalLabels();

  const availableCategories = election_candidates_labels?.category_types || [];

  // CMS-driven labels destructured directly, with safe fallbacks for every string
  const {
    catergory_label: categoryLabel = 'View By:',
    select_voting_area_label: selectAreaLabel = 'Select Voting Area:',
    associations_view_label: associationsViewLabel,
    district_view_label: districtViewLabel,
    voting_area_label: votingAreaLabel = 'Voting Area',
    votes_label: votesLabel = 'Votes',
    view_bio: viewBioLabel = 'View Bio',
    no_candidates_for_the_category: noCandidatesForTheCategory,
    no_candidates_for_the_position: noCandidatesForThePosition,
  } = election_candidates_labels ?? {};

  const [activeCategory, setActiveCategory] = useState(availableCategories[0]);
  const [activeArea, setActiveArea] = useState<string>(votingAreas[0]);

  // Association and District each author their own position list, kept fully separate
  const activePositions =
    activeCategory === 'Association' ? associationPositions : districtPositions;

  // Filter to the active category + voting area, then group by position
  const positionGroups = useMemo<PositionGroup[]>(() => {
    const matching = candidates.filter(
      (candidate) =>
        candidate.category === activeCategory &&
        (candidate.voting_area as string[]).includes(activeArea)
    );

    // CMS-authored positions take priority; otherwise positions are derived from the candidates
    const fromCMS = activePositions.length > 0;

    // Establish position order: authored positions first, else order of appearance
    const orderedPositions: { uid: string; title: string }[] = fromCMS
      ? activePositions
          .filter((position) => !!position.uid)
          .map((position) => ({ uid: position.uid!, title: position.title }))
      : [];

    if (orderedPositions.length === 0) {
      const seen = new Set<string>();
      for (const candidate of matching) {
        const position = candidate.position?.[0];
        if (position?.uid && !seen.has(position.uid)) {
          seen.add(position.uid);
          orderedPositions.push({ uid: position.uid, title: position.title });
        }
      }
    }

    // Bucket candidates by position uid and sort each bucket by votes (highest first);
    // positions with no matching candidates are kept so their empty state still renders
    return orderedPositions.map(({ uid, title }) => ({
      uid,
      title,
      candidates: matching
        .filter((candidate) => candidate.position?.[0]?.uid === uid)
        .sort((a, b) => (b.number_of_votes ?? 0) - (a.number_of_votes ?? 0)),
    }));
  }, [candidates, activeCategory, activeArea, activePositions]);

  const styles = electionCandidatesVariants();

  // Component identifier shared by the Click events fired from this component
  const clickLocation = `${componentName}_${_metadata?.uid}`;

  // Compose the active toggle button class without duplicating the base slot
  const toggleClass = (isActive: boolean, className?: string) =>
    `${styles.toggleButton({ className })} ${isActive ? styles.toggleButtonActive() : ''}`;

  if (availableCategories.length === 0 || votingAreas.length === 0) return null;

  return (
    <Container componentName={componentName} metaId={_metadata?.uid}>
      {/* Control bar: category toggle (left) + voting-area selector (right) */}
      <div className={styles.controlBar()}>
        <div className={styles.controlGroup()}>
          <span className={styles.controlLabel()}>{categoryLabel}</span>
          <div className={styles.buttonRow()} role="group" aria-label={categoryLabel}>
            {availableCategories.map((category) => (
              <button
                key={category}
                type="button"
                className={toggleClass(category === activeCategory, 'w-auto! px-3')}
                aria-pressed={category === activeCategory}
                onClick={() => {
                  trackAdobeClick({ clickName: category, clickType: 'category-select', clickLocation });
                  setActiveCategory(category);
                }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.controlGroup({ scrollable: true })}>
          <span className={styles.controlLabel({ className: 'text-left xm:text-right' })}>
            {selectAreaLabel}
          </span>
          <div
            className={styles.buttonRow({ scrollable: true })}
            role="group"
            aria-label={selectAreaLabel}
          >
            {votingAreas.map((area) => (
              <button
                key={area}
                type="button"
                className={toggleClass(area === activeArea)}
                aria-pressed={area === activeArea}
                aria-label={`${votingAreaLabel} ${area}`}
                onClick={() => {
                  trackAdobeClick({
                    clickName: `${votingAreaLabel} ${area}`,
                    clickType: 'voting-area-select',
                    clickLocation,
                  });
                  setActiveArea(area);
                }}
              >
                {area}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Heading: "The {category} Elections" + active voting-area pill */}
      <div className={styles.headingRow()}>
        <h3 className={styles.heading()}>
          {activeCategory === 'Association' ? associationsViewLabel : districtViewLabel}
        </h3>
        <span className={styles.areaBadge()}>{`${votingAreaLabel} ${activeArea}`}</span>
      </div>

      {/* Position groups, each with its vote-sorted candidate cards */}
      {positionGroups.length === 0 ? (
        <p className={styles.emptyState()}>{noCandidatesForTheCategory}</p>
      ) : (
        positionGroups.map((group) => (
          <section
            key={group.uid}
            className={styles.positionGroup()}
            aria-label={`position-${group.title}`}
          >
            <h4 className={styles.positionTitle()}>{group.title}</h4>
            {group.candidates.length === 0 ? (
              <p className={styles.emptyState()}>{noCandidatesForThePosition}</p>
            ) : (
              <div className={styles.candidateList()}>
                {group.candidates.map((candidate) => {
                  // A card is clickable (and gets the hover effect) only when a bio PDF exists
                  const hasBio = !!candidate.bio_file?.url;
                  return (
                    <article
                      key={candidate.uid}
                      className={styles.candidateCard({ clickable: hasBio })}
                    >
                      <div className={styles.candidateInfo()}>
                        <h4
                          className={styles.candidateName()}
                          {...getCSLPAttributes(candidate.$?.title)}
                        >
                          {getCandidateName(candidate)}
                        </h4>
                        <div className={styles.metaRow()}>
                          {candidate.winner && (
                            <span
                              className={styles.winnerBadge()}
                              {...getCSLPAttributes(candidate.$?.winner)}
                            >
                              {candidate.winner}
                            </span>
                          )}
                          {candidate.number_of_votes != null && (
                            <span
                              className={styles.votesBadge()}
                              {...getCSLPAttributes(candidate.$?.number_of_votes)}
                            >
                              {`${formatVotes(candidate.number_of_votes)} ${votesLabel}`}
                            </span>
                          )}
                        </div>
                      </div>
                      {candidate.bio_file?.url && (
                        <a
                          href={candidate.bio_file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.bioLink()}
                          aria-label={`${viewBioLabel} — ${getCandidateName(candidate)}`}
                          onClick={(e) =>
                            trackAdobeDownload(
                              {
                                clickName: `${viewBioLabel} — ${getCandidateName(candidate)}`,
                                documentName: getDocumentNameFromUrl(candidate.bio_file!.url),
                                clickType: 'bio-link',
                                clickLocation: `${componentName}_${_metadata?.uid}`,
                              },
                              e
                            )
                          }
                          {...getCSLPAttributes(candidate.$?.bio_file)}
                        >
                          <span className={styles.bioButton()}>{viewBioLabel}</span>
                        </a>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        ))
      )}
    </Container>
  );
};
